<template>
    <div class="flex flex-column align-items-center w-full mb-8">
        <div class="px-3 lg:px-6 py-5 w-full bg-white shadow-1 border-round">

            <!-- ── DESKTOP LAYOUT ── -->
            <div class="hidden lg:flex flex-column w-full">

                <!-- Package headers row -->
                <div class="flex flex-row w-full border-bottom-1 border-gray-200">
                    <!-- Spacer aligned with the label column -->
                    <div class="feature-label-col flex-1 pr-1"></div>

                    <!-- One header per package -->
                    <div v-for="(pkg, index) in packages" :key="pkg.id + index"
                        class="flex flex-column align-items-center text-center border-left-1 border-gray-200 flex-1 relative">

                        <div v-if="pkg.popular" class="absolute" style="top: -15px;">
                            <span class="bg-green-100 text-green-600 border-round-2xl px-3 py-1 text-xs font-semibold border-1 border-green-300">
                                Popular
                            </span>
                        </div>

                        <div class="pkg-header-title w-full mt-4 px-2 lg:px-4">
                            <h3 class="text-lg lg:text-base xl:text-lg font-medium text-800 m-0">{{ pkg.name }}</h3>
                        </div>

                        <div class="pkg-header-price w-full mt-3 mb-2 px-4 lg:px-4 flex align-items-baseline justify-content-center">
                            <span class="text-xl font-medium mr-1 text-800">Rs.</span>
                            <span class="text-6xl lg:text-5xl xl:text-6xl font-bold H6-Medium">{{ pkg.priceLabel }}</span>
                        </div>

                        <p class="text-600 text-xs mb-4">per month/ user</p>

                        <div class="pkg-header-button w-full px-4 lg:px-4 mb-2">
                            <Button
                                :label="getButtonLabel(pkg.id)"
                                :class="getButtonClass(pkg.id, pkg.popular)"
                                :disabled="isButtonDisabled(pkg.id)"
                                @click="selectPackage(pkg, getButtonAction(pkg.id))"
                            />
                        </div>
                        <p v-if="getButtonAction(pkg.id) === 'locked'" class="text-xs text-500 mb-4 px-4 text-center m-0">
                            Change plan after completing onboarding
                        </p>
                        <div v-else class="mb-6"></div>
                    </div>
                </div>

                
                <div class="w-full">
                    <div v-for="def in featureDefinitions" :key="def.key"
                        class="flex flex-row border-bottom-1 border-gray-200">

                        <!-- Label cell -->
                        <div class="feature-label-col flex-1 feature-row justify-content-start font-normal text-500 pl-2 text-sm">
                            {{ def.label }}
                            <i v-if="def.info" class="pi pi-info-circle text-xs ml-1 cursor-pointer" :title="def.info"></i>
                        </div>

                        <!-- Value cell per package -->
                        <div v-for="pkg in packages" :key="pkg.id"
                            class="feature-row flex-1 border-left-1 border-gray-200 justify-content-center">
                            <span v-if="typeof pkg.features[def.key] === 'string' || typeof pkg.features[def.key] === 'number'"
                                class="text-sm text-600 font-normal">
                                {{ pkg.features[def.key] }}
                            </span>
                            <template v-else>
                                <i class="pi pi-check text-600 text-sm" v-if="pkg.features[def.key] === true"></i>
                                <i class="pi pi-times text-600 text-sm" v-else></i>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ── MOBILE LAYOUT ── -->
            <div class="lg:hidden flex flex-column w-full">
                <div v-for="(pkg, index) in packages" :key="pkg.id + index"
                    class="flex flex-column align-items-center text-center mb-6 relative">

                    <div v-if="pkg.popular" class="absolute" style="top: -15px;">
                        <span class="bg-green-100 text-green-600 border-round-2xl px-3 py-1 text-xs font-semibold border-1 border-green-300">
                            Popular
                        </span>
                    </div>

                    <div class="pkg-header-title w-full mt-4 px-4">
                        <h3 class="text-lg font-medium text-800 m-0">{{ pkg.name }}</h3>
                    </div>

                    <div class="pkg-header-price w-full mt-3 mb-2 px-4 flex align-items-baseline justify-content-center">
                        <span class="text-xl font-medium mr-1 text-800">Rs.</span>
                        <span class="text-5xl font-bold H6-Medium">{{ pkg.priceLabel }}</span>
                    </div>

                    <p class="text-600 text-sm mb-6">per month/ user</p>

                    <div class="pkg-header-button w-full px-4 mb-2">
                        <Button
                            :label="getButtonLabel(pkg.id)"
                            :class="getButtonClass(pkg.id, pkg.popular)"
                            :disabled="isButtonDisabled(pkg.id)"
                            @click="selectPackage(pkg, getButtonAction(pkg.id))"
                        />
                    </div>
                    <p v-if="getButtonAction(pkg.id) === 'locked'" class="text-xs text-500 mb-4 px-4 text-center m-0">
                        Change plan after completing onboarding
                    </p>
                    <div v-else class="mb-4"></div>

                    <div class="w-full mt-4 border-round">
                        <div v-for="def in featureDefinitions" :key="def.key"
                            class="flex justify-content-between align-items-center py-3 px-3 border-bottom-1 border-gray-200">
                            <span class="text-sm text-500 font-normal text-left">{{ def.label }}</span>
                            <span>
                                <span v-if="typeof pkg.features[def.key] === 'string' || typeof pkg.features[def.key] === 'number'"
                                    class="text-sm text-600 font-normal">
                                    {{ pkg.features[def.key] }}
                                </span>
                                <template v-else>
                                    <i class="pi pi-check text-600 text-sm" v-if="pkg.features[def.key] === true"></i>
                                    <i class="pi pi-times text-600 text-sm" v-else></i>
                                </template>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <!-- Disclaimer -->
        <div class="text-center w-full max-w-4xl pt-6 pb-2">
            <h3 class="disclaimer-heading text-lg font-semibold mb-2" style="color: #1FC9A3;">Disclaimer</h3>
            <p class="text-700 text-sm m-0 mb-2">
                All payments must be processed through the platform. ILearn'd Tutors is not responsible for issues caused by third-party payment providers or user error.
            </p>
            <p class="text-700 text-sm m-0">
                Feature availability may vary depending on your subscription plan and development stage.
            </p>
        </div>
        
    </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';
import Button from 'primevue/button';

const props = defineProps({
    // 'onboarding' or 'settings'
    mode: {
        type: String,
        default: 'onboarding'
    },
    // The ID of the currently active plan (only matters if mode is 'settings')
    currentPlanId: {
        type: String,
        default: null
    },
    // Optional: Pass these as props if the text changes between views
    featureDefinitions: {
        type: Array,
        required: true
    },
    packages: {
        type: Array,
        required: true
    },
    disableActions: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['package-action']);

// --- Dynamic Button Logic ---
const getButtonAction = (pkgId) => {
    if (props.mode === 'onboarding') {
        if (!props.currentPlanId) return 'start';
        return pkgId === props.currentPlanId ? 'current' : 'locked';
    }
    if (pkgId === props.currentPlanId) return 'current';
    // Use array index (packages sorted by displayOrder from API) for upgrade/downgrade logic
    const currentIdx = props.packages.findIndex(p => p.id === props.currentPlanId);
    const targetIdx  = props.packages.findIndex(p => p.id === pkgId);
    return targetIdx > currentIdx ? 'upgrade' : 'downgrade';
};

const getButtonLabel = (pkgId) => {
    const pkg = props.packages.find(p => p.id === pkgId);
    if (pkg?.actionLabel) return pkg.actionLabel;

    const action = getButtonAction(pkgId);
    switch (action) {
        case 'start':    return 'Get Started';
        case 'current':  return props.mode === 'onboarding' ? 'Plan Selected' : 'Your current plan';
        case 'locked':   return 'Get Started';
        case 'upgrade':  return 'Upgrade';
        case 'downgrade':return 'Downgrade';
        default:         return 'Select';
    }
};

const getButtonClass = (pkgId, isPopular) => {
    const action = getButtonAction(pkgId);
    if (action === 'current' && props.mode === 'onboarding')
        return 'custom-teal-btn w-full opacity-80 cursor-default';
    if (action === 'current')
        return 'p-button-outlined p-button-secondary w-full opacity-60';
    if (action === 'locked')
        return 'p-button-outlined p-button-secondary w-full opacity-40';
    if (isPopular)
        return 'custom-teal-btn w-full';
    return 'p-button-outlined p-button-secondary w-full text-700';
};

const isButtonDisabled = (pkgId) => {
    if (props.disableActions) return true;

    const pkg = props.packages.find(p => p.id === pkgId);
    if (pkg && typeof pkg.disabled === 'boolean') {
        return pkg.disabled;
    }

    const action = getButtonAction(pkgId);
    return action === 'current' || action === 'locked';
};

const selectPackage = (pkg, action) => {
    emit('package-action', { package: pkg, action: action });
};
</script>

<style scoped>

.feature-row {
    min-height: 55px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 4px;
    word-break: break-word;
}

.text-500 {
    color: #7c858d !important;
}

.pkg-header-title {
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pkg-header-price {
    height: 60px;
}

.pkg-header-button {
    height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pkg-header-button :deep(.p-button) {
    font-size: 1rem;
    border-radius: 4px;
}

.custom-teal-btn {
    background-color: var(--teal-500) !important;
    border: 1px solid var(--teal-500) !important;
    color: white !important;
    font-weight: 600;
}

.custom-teal-btn:hover {
    background-color: var(--teal-600) !important;
    border-color: var(--teal-600) !important;
}

.p-button-outlined {
    background-color: var(--accent-50) !important;
    color: black !important;
    font-weight: 600;
}

.p-button-outlined:not(:disabled):hover {
    background-color: var(--accent-100) !important;
}
</style>